import {
  Component,
  DestroyRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import type {
  ConnectionServiceOptions,
  ConnectionState,
} from 'ng-connection-service';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from '@angular/common/http';
import { HeaderComponent } from './layout/header/header.component';
import { FooterComponent } from './layout/footer/footer.component';
import { AppShellHeaderComponent } from './layout/app-shell/app-shell-header';

type ShellMode = 'workspace' | 'app' | 'marketing';

const APP_SHELL_PREFIXES = ['/engagements', '/admin', '/checkout'];

const HEARTBEAT_URL = '/favicon.ico';
const HEARTBEAT_INTERVAL_MS = 30000;

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    AppShellHeaderComponent,
    CommonModule,
    MatButtonModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = 'Angularize';

  shellMode: ShellMode = 'marketing';

  isOnline = true;
  status = 'ONLINE';
  currentState!: ConnectionState;

  cells: string[] = Array(9).fill('');
  currentPlayer: 'X' | 'O' = 'X';
  winner = false;
  winnerMessage = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.shellMode = this.resolveShellMode(event.urlAfterRedirects);
      });
  }

  private resolveShellMode(url: string): ShellMode {
    if (url.startsWith('/projects')) return 'workspace';
    if (APP_SHELL_PREFIXES.some((p) => url.startsWith(p))) return 'app';
    return 'marketing';
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    void this.monitorConnection();
  }

  private async monitorConnection(): Promise<void> {
    const { ConnectionService } = await import('ng-connection-service');

    const options: ConnectionServiceOptions = {
      heartbeatUrl: HEARTBEAT_URL,
      heartbeatInterval: HEARTBEAT_INTERVAL_MS,
    };

    const connectionService = new ConnectionService(this.http, options);

    connectionService
      .monitor()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newState: ConnectionState) => {
        this.currentState = newState;
        this.isOnline = newState.hasNetworkConnection;
        this.status = newState.hasNetworkConnection ? 'ONLINE' : 'OFFLINE';
      });
  }

  getCellBackgroundColor(index: number): string {
    if (this.cells[index] === 'O') return '#2B2B2B';
    if (this.cells[index] === 'X') return '#C3002F';
    return '#ffffff';
  }

  getCellTextColor(index: number): string {
    const bg = this.getCellBackgroundColor(index);
    const darkColors = ['#2B2B2B', '#C3002F', '#666666', '#808080'];
    return darkColors.includes(bg.toUpperCase()) ? '#ffffff' : '#000000';
  }

  makeMove(index: number): void {
    if (this.cells[index] || this.winner) return;

    this.cells[index] = 'X';

    if (this.checkWinner('X')) {
      this.winner = true;
      this.winnerMessage = ' 😎 You win! 🎉';
      return;
    }

    if (!this.cells.includes('')) {
      this.winner = true;
      this.winnerMessage = "🎉 It's a draw! 🤝";
      return;
    }

    setTimeout(() => this.computerMove(), 300);
  }

  computerMove(): void {
    if (this.winner) return;

    const move = this.getBestMove();
    if (move !== -1) this.cells[move] = 'O';

    if (this.checkWinner('O')) {
      this.winner = true;
      this.winnerMessage = '😎 Computer wins! 🤖';
    } else if (!this.cells.includes('')) {
      this.winner = true;
      this.winnerMessage = "🎉 It's a draw! 🤝";
    }
  }

  getBestMove(): number {
    for (let i = 0; i < 9; i++) {
      if (!this.cells[i]) {
        this.cells[i] = 'O';
        if (this.checkWinner('O')) return i;
        this.cells[i] = '';
      }
    }

    for (let i = 0; i < 9; i++) {
      if (!this.cells[i]) {
        this.cells[i] = 'X';
        const blocks = this.checkWinner('X');
        this.cells[i] = '';
        if (blocks) return i;
      }
    }

    const emptyCells = this.cells
      .map((v, i) => (v === '' ? i : -1))
      .filter((i) => i !== -1);

    if (emptyCells.length) {
      return emptyCells[Math.floor(Math.random() * emptyCells.length)];
    }

    return -1;
  }

  checkWinner(player: 'X' | 'O'): boolean {
    const combos = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ];

    return combos.some(
      ([a, b, c]) =>
        this.cells[a] === player &&
        this.cells[a] === this.cells[b] &&
        this.cells[a] === this.cells[c],
    );
  }

  resetGame(): void {
    this.cells = Array(9).fill('');
    this.currentPlayer = 'X';
    this.winner = false;
    this.winnerMessage = '';
  }
}